import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { v4 as uuidv4 } from 'uuid';
import { GlobalMessageService } from '../../global-services/global-message.service';
import { GlobalLoadingService } from '../../global-services/global-loading.service';
import { CountryModel } from '../../global-data-models/country.model';
import { CountryService } from '../../global-services/country.service';
import { CreateOrgModel, OrgModel, UpdateOrgModel, REFRACTIVE_INSTRUMENTS, ASTIGMATISM_INSTRUMENTS, CATARACT_INSTRUMENTS } from '../../global-data-models/org.model';
import { OrgService } from '../../global-services/org.service';
import { Router } from '@angular/router';
import { MeService } from '../../global-services/me.service';
import { TimezoneModel } from '../../global-data-models/timezone.model';
import { TimezoneService } from '../../global-services/timezone.service';

export type CrudOrgMode = 'create' | 'read' | 'update';

export interface CrudOrgUiState {
  mode: CrudOrgMode;
  form: FormGroup;
  countries: CountryModel[];
  timezones: TimezoneModel[];
  error?: string;
}

@Injectable({ providedIn: 'root' })
export class CrudOrgFacade {
  state$;
  public form: FormGroup;
  private stateSubject: BehaviorSubject<CrudOrgUiState>;

  constructor(
    private fb: FormBuilder,
    private messageService: GlobalMessageService,
    private loadingService: GlobalLoadingService,
    private countryService: CountryService,
    private orgService: OrgService,
    private userService: MeService,
    private router: Router,
    private timezoneService: TimezoneService
  ) {
    this.form = this.createForm();
    this.stateSubject = new BehaviorSubject<CrudOrgUiState>({
      mode: 'create',
      form: this.form,
      countries: [],
      timezones: []
    });
    this.state$ = this.stateSubject.asObservable();
  }

  async begin(mode: CrudOrgMode = 'create', orgId?: string) {
    try {
      this.loadingService.show();
      const countries = await this.countryService.getAllCountries();
      const timezones = await this.timezoneService.getAllTimezones();

      if (mode === 'create') {
        this.form = this.createForm();
        this.stateSubject.next({
          mode,
          form: this.form,
          countries,
          timezones
        });
        return;
      }

      if (!orgId) {
        throw new Error('Organisation ID is required for read/update mode');
      }

      // orgData = await this.orgService.getOrgById(orgId);
      const orgData = await this.orgService.getOrg(orgId);

      this.form = this.createForm(orgData);
      if (mode === 'read') {
        this.form.disable();
        this.stateSubject.next({ mode, form: this.form, countries, timezones });
        return;
      }

      if (mode === 'update') {
        this.form.enable();
        this.form.get("page1")?.get('id')?.disable();
        this.stateSubject.next({ mode, form: this.form, countries, timezones });
        return;
      }

      throw new Error('Invalid mode');
    } catch (error) {
      const errorMessage = (error as any).message || 'An unknown error occurred';
      this.messageService.showError('Error', errorMessage);
      this.stateSubject.next({ mode, form: this.form, countries: [], timezones: [] });
    } finally {
      this.loadingService.hide();
    }
  }

  private createForm(orgData?: OrgModel): FormGroup {
    // Helper function to determine if an instrument is a predefined one or custom
    const getInstrumentValue = (instrument: string, predefinedList: string[]) => {
      return predefinedList.includes(instrument) ? instrument : 'OTHER';
    };

    const getOtherValue = (instrument: string, predefinedList: string[]) => {
      return predefinedList.includes(instrument) ? '' : instrument;
    };

    return this.fb.group({
      page1: this.fb.group({
        id: [{ value: orgData?.id || uuidv4(), disabled: true }, Validators.required],
        legalName: [orgData?.legalName || '', Validators.required],
        displayName: [orgData?.displayName || '', Validators.required],
        contactEmail: [orgData?.contactEmail || '', [Validators.required, Validators.email]],
        contactTelephone: [orgData?.contactTelephone || ''],
        addressLine1: [orgData?.addressLine1 || '', Validators.required],
        addressLine2: [orgData?.addressLine2 || ''],
        city: [orgData?.city || '', Validators.required],
        postcode: [orgData?.postcode || ''],
        country: [orgData?.country_id, Validators.required],
        timezoneId: [orgData?.timezone_id, Validators.required],
      }),

      page2: this.fb.group({
        refractiveInstrument: [
          orgData?.instruments[0]
            ? getInstrumentValue(orgData.instruments[0], REFRACTIVE_INSTRUMENTS)
            : '',
          Validators.required
        ],
        refractiveInstrumentOther: [
          orgData?.instruments[0]
            ? getOtherValue(orgData.instruments[0], REFRACTIVE_INSTRUMENTS)
            : ''
        ],
        astigmatismInstrument: [
          orgData?.instruments[1]
            ? getInstrumentValue(orgData.instruments[1], ASTIGMATISM_INSTRUMENTS)
            : '',
          Validators.required
        ],
        astigmatismInstrumentOther: [
          orgData?.instruments[1]
            ? getOtherValue(orgData.instruments[1], ASTIGMATISM_INSTRUMENTS)
            : ''
        ],
        cataractInstrument: [
          orgData?.instruments[2]
            ? getInstrumentValue(orgData.instruments[2], CATARACT_INSTRUMENTS)
            : '',
          Validators.required
        ],
        cataractInstrumentOther: [
          orgData?.instruments[2]
            ? getOtherValue(orgData.instruments[2], CATARACT_INSTRUMENTS)
            : ''
        ]
      })
    });
  }

  markFormTouchedAndCheckValidity(): boolean {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      return true;
    }
    return false;
  }

  async saveButtonPressed(): Promise<boolean> {
    try {
      this.loadingService.show();

      const mode = this.stateSubject.getValue().mode;

      if (mode === 'read') {
        throw new Error('Cannot save in read mode');
      }

      if (!this.markFormTouchedAndCheckValidity()) {
        this.messageService.showError('Error', 'Not all form fields are filled or valid');
        return false; // Indicate failure
      }

      const org = this.transformFormValuesToOrgModel();

      if (mode === 'create') {
        await this.createOrg(org as CreateOrgModel);
        return true; // Indicate success
      }

      if (mode === 'update') {
        await this.updateOrg(org as UpdateOrgModel);
        return true; // Indicate success
      }

      throw new Error('Invalid mode');
    } catch (error) {
      const errorObj = error as any;
      const message = errorObj.error?.message || errorObj.message || 'unknown error';
      this.messageService.showError('Error', `${message}`);
      return false; // Indicate failure
    } finally {
      this.loadingService.hide();
    }
  }

  private async createOrg(org: CreateOrgModel) {
    const createOrg: CreateOrgModel = {
      ...org,
      autoAssignClinicAdmin: true,
    };

    await this.orgService.createOrg(createOrg);
    this.messageService.showSuccess('Organisation Created', 'The organisation has been created successfully.');

    await this.userService.refreshMe();
    this.router.navigate(['/home']);
  }

  private async updateOrg(org: UpdateOrgModel) {
    const updateOrg: UpdateOrgModel = {
      ...org,
    };
    // Let potential errors propagate up to saveButtonPressed
    await this.orgService.updateOrg(org.id!, updateOrg);
    this.messageService.showSuccess('Organisation Updated', 'The organisation has been updated successfully.');
  }

  private transformFormValuesToOrgModel(): CreateOrgModel | UpdateOrgModel {
    const rawValues = this.form.getRawValue();

    // Handle "Other" instruments by using the custom input value if "OTHER" is selected
    const refractiveInstrument = rawValues.page2.refractiveInstrument === 'OTHER'
      ? rawValues.page2.refractiveInstrumentOther
      : rawValues.page2.refractiveInstrument;

    const astigmatismInstrument = rawValues.page2.astigmatismInstrument === 'OTHER'
      ? rawValues.page2.astigmatismInstrumentOther
      : rawValues.page2.astigmatismInstrument;

    const cataractInstrument = rawValues.page2.cataractInstrument === 'OTHER'
      ? rawValues.page2.cataractInstrumentOther
      : rawValues.page2.cataractInstrument;

    const org: CreateOrgModel | UpdateOrgModel = {
      id: rawValues.page1.id,
      legalName: rawValues.page1.legalName,
      displayName: rawValues.page1.displayName,
      contactEmail: rawValues.page1.contactEmail,
      contactTelephone: rawValues.page1.contactTelephone,
      addressLine1: rawValues.page1.addressLine1,
      addressLine2: rawValues.page1.addressLine2,
      city: rawValues.page1.city,
      postcode: rawValues.page1.postcode,
      country_id: Number(rawValues.page1.country),
      timezone_id: Number(rawValues.page1.timezoneId),
      instruments: [
        refractiveInstrument,
        astigmatismInstrument,
        cataractInstrument
      ]
    };

    return org;
  }
}
